import { useReducer, Reducer, useEffect, useState } from 'react';
import { EventListener } from 'react-commons';
import { GameData } from '../drupal/models/Games';
import { useGameList } from './useGameList';
import { PagerData } from '@/lib/util/pager';

export const NUM_GAMES_PER_SEARCH = 10;
export const MIN_QUERY_LENGTH = 3;

export const SORT_BY = {
  '-most-popular': 'Most Popular',
  '-field_aggregate_game_rating': 'Highest Rated',
  '-field_first_release_date': 'Newest',
};

export enum DailySearchCriteriaActionType {
  SET_CRITERIA,
  SET_GENRES,
  SET_QUERY,
  SET_SORT_BY,
  SET_PLATFORMS,
}

export interface DailySearchCriteriaAction {
  type: DailySearchCriteriaActionType
  payload: any
}

export type SortBy = keyof typeof SORT_BY;

export const DEFAULT_SORT_BY = '-most-popular' as SortBy;

export type ContentType = 'free_game' | 'paid_game';

export interface DailySearchCriteria {
  query?: string
  genres: string[]
  contentTypes: ContentType[]
  sortBy: SortBy
  method?: (limit: number, offset: number, criteria: Omit<DailySearchConfig, 'method'>, sortOrder: 'ASC' | 'DESC') => Promise<GameData[] | { games: GameData[], pager: PagerData }>
}

export interface DailySearchConfig extends DailySearchCriteria {
  numItemsPerPage?: number
  initialNumItems?: number
}

export interface useDailyGamesByCriteriaOptions {
  initialGames: GameData[]
  initialCriteria: DailySearchConfig
}

export const gameSearchEvents = new EventListener<{
  queryChanged: (query: string) => void
  setQuery: (query: string) => void
}>();

export async function searchForGames (criteria: DailySearchConfig, page = 0) {
  const limit = criteria.numItemsPerPage || NUM_GAMES_PER_SEARCH;
  const offset = (criteria.numItemsPerPage || NUM_GAMES_PER_SEARCH) * page;

  const sortOrder = criteria.sortBy.startsWith('-') 
    ? 'DESC' 
    : 'ASC';

  try {
    const result = await criteria.method?.(limit, offset, criteria, sortOrder);
    if (Array.isArray(result)) {
      return {
        games: result,
        numGames: 0,
      };
    } else {
      return {
        games: result.games,
        numGames: result.pager.totalItems,
      };
    }
  } catch (error) {
    throw error;
  }
}

export function formatQuery (query: string) {
  return query.replace(/\+/g, ' ');
}

export function useDailyGamesByCriteria (options: useDailyGamesByCriteriaOptions) {
  // Search criteria
  const [ DailySearchCriteria, dispatchDailySearchCriteria ] = useReducer<Reducer<DailySearchConfig, DailySearchCriteriaAction>>((state, action) => {
    switch (action.type) {
    case DailySearchCriteriaActionType.SET_CRITERIA: {
      return {
        ...state,
        ...action.payload
      };
    }
    case DailySearchCriteriaActionType.SET_GENRES: {
      return {
        ...state,
        genres: action.payload
      };
    }
    case DailySearchCriteriaActionType.SET_QUERY: {
      return {
        ...state,
        query: action.payload
      };
    }
    case DailySearchCriteriaActionType.SET_SORT_BY: {
      return {
        ...state,
        sortBy: action.payload,
      };
    }
    case DailySearchCriteriaActionType.SET_PLATFORMS: {
      return {
        ...state,
        contentTypes: action.payload,
      };
    }
    }
  }, { ...options.initialCriteria });

  const [ numGames, setNumGames ] = useState(options.initialCriteria.initialNumItems || 0);

  // Setup game list
  const { 
    games,
    page,
    isLoading,
    hasReachedEnd,
    showMoreGames,
    setCurrentPage,
    newSearch,
  } = useGameList<DailySearchConfig>({
    fetchOnFirstRender: false,
    initialGames: options.initialGames,
    numGamesPerRequest: DailySearchCriteria.numItemsPerPage || NUM_GAMES_PER_SEARCH,
    replaceGamesOnSearch: !!DailySearchCriteria.numItemsPerPage,
    criteria: DailySearchCriteria,
    isCriteriaValid: (criteria) => {
      return !criteria.query || criteria.query.length >= MIN_QUERY_LENGTH;
    },
    fetchMoreGames: async (criteria, page) => {
      const result = await searchForGames(criteria, page);
      setNumGames(result.numGames);
      return result.games;
    }
  });

  // Methods
  const updateSearch = (type: DailySearchCriteriaActionType, payload: any) => {
    newSearch(() => {
      dispatchDailySearchCriteria({
        type,
        payload,
      });
    });

    return { ...DailySearchCriteria };
  };

  // Keep query in sync with search state
  useEffect(() => {
    const setQueryHandler = (nextQuery: string) => updateSearch(DailySearchCriteriaActionType.SET_CRITERIA, { query: nextQuery });
    gameSearchEvents.on('setQuery', setQueryHandler);

    return () => {
      gameSearchEvents.off('setQuery', setQueryHandler);
    };
  });

  const getCriteria = () => ({ ...DailySearchCriteria });

  return {
    games,
    page,
    isLoading,
    hasReachedEnd,
    showMoreGames,
    DailySearchCriteria,
    updateSearch,
    newSearch,
    setCurrentPage,
    numGames,
    criteria: {
      ...DailySearchCriteria
    }
  };
}
